import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { useApolloClient } from '@apollo/client';
import { memberfulQueries } from '../../../graphql/queries';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '80vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 1
};

const MemberfulHistoryDetail = ({ id, onCancelClick }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const client = useApolloClient();

  useEffect(() => {
    if (id) {
      setLoading(true);
      setError(null);
      client.query({
        query: memberfulQueries.MemberfulMigrationById(id),
        fetchPolicy: 'no-cache'
      }).then(result => {
        setRecord(result.data.memberfulMigrationById);
        setLoading(false);
      }).catch(err => {
        setError(err.message);
        setLoading(false);
      });
    }
  }, [id]);

  if (loading) {
    return (
      <Box sx={modalStyle}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant='body2' sx={{ mt: 1 }}>Loading migration record...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={modalStyle}>
        <Alert severity='error'>{error}</Alert>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='outlined' onClick={onCancelClick}>Close</Button>
        </Box>
      </Box>
    );
  }

  if (!record) {
    return (
      <Box sx={modalStyle}>
        <Typography>Migration record not found.</Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='outlined' onClick={onCancelClick}>Close</Button>
        </Box>
      </Box>
    );
  }

  const statusColor = record.status === 'success'
    ? 'success'
    : record.status === 'partial' ? 'warning' : 'error';

  const subscriptions = typeof record.subscriptionsMigrated === 'string'
    ? JSON.parse(record.subscriptionsMigrated)
    : record.subscriptionsMigrated;

  const warnings = typeof record.warnings === 'string'
    ? JSON.parse(record.warnings)
    : record.warnings;

  const errors = typeof record.errors === 'string'
    ? JSON.parse(record.errors)
    : record.errors;

  const memberfulData = typeof record.memberfulData === 'string'
    ? JSON.parse(record.memberfulData)
    : record.memberfulData;

  return (
    <Box sx={modalStyle}>
      <Typography variant='h6' sx={{ mb: 2 }}>Migration Detail</Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant='subtitle2' color='textSecondary'>Status</Typography>
        <Chip label={record.status} color={statusColor} size='small' />
        <Chip label={record.action} variant='outlined' size='small' sx={{ ml: 1 }} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant='subtitle2' color='textSecondary'>Memberful Member</Typography>
      <Typography variant='body1'>
        {record.memberfulFullName} ({record.memberfulEmail})
      </Typography>
      <Typography variant='body2' color='textSecondary'>
        Memberful ID: {record.memberfulMemberId}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant='subtitle2' color='textSecondary'>BridgeBee User</Typography>
      <Typography variant='body1'>
        User ID: {record.bbUserId || 'N/A'}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant='subtitle2' color='textSecondary'>Migrated By</Typography>
      <Typography variant='body1'>
        Admin User ID: {record.migratedBy || 'Script'}
      </Typography>
      <Typography variant='body2' color='textSecondary'>
        Date: {record.createdAt
          ? new Date(/^\d+$/.test(String(record.createdAt))
            ? parseInt(record.createdAt)
            : record.createdAt
          ).toLocaleString()
          : 'N/A'}
      </Typography>

      {subscriptions && subscriptions.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant='subtitle2' color='textSecondary' sx={{ mb: 1 }}>
            Subscriptions Migrated
          </Typography>
          <TableContainer component={Paper} variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Memberful Plan</TableCell>
                  <TableCell>BB Subscription</TableCell>
                  <TableCell>Plan Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((sub, index) => (
                  <TableRow key={index}>
                    <TableCell>{sub.memberfulPlanName}</TableCell>
                    <TableCell>{sub.bbSubscriptionTitle} (ID: {sub.bbSubscriptionId})</TableCell>
                    <TableCell>{sub.planType}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {warnings && warnings.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' color='textSecondary' sx={{ mb: 1 }}>Warnings</Typography>
          {warnings.map((warning, index) => (
            <Alert key={index} severity='warning' sx={{ mb: 1 }}>
              {warning}
            </Alert>
          ))}
        </Box>
      )}

      {errors && errors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' color='textSecondary' sx={{ mb: 1 }}>Errors</Typography>
          {errors.map((err, index) => (
            <Alert key={index} severity='error' sx={{ mb: 1 }}>
              {typeof err === 'string' ? err : JSON.stringify(err)}
            </Alert>
          ))}
        </Box>
      )}

      {memberfulData && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant='subtitle2' color='textSecondary' sx={{ mb: 1 }}>
            Raw Memberful Data
          </Typography>
          <Paper variant='outlined' sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
            <pre style={{ margin: 0, fontSize: '0.75rem' }}>
              {JSON.stringify(memberfulData, null, 2)}
            </pre>
          </Paper>
        </>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant='outlined' onClick={onCancelClick}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

MemberfulHistoryDetail.propTypes = {
  id: PropTypes.number.isRequired,
  onCancelClick: PropTypes.func.isRequired
};

export default MemberfulHistoryDetail;
